import { SpellUsageBossOption, SpellUsageFightFilter } from '../types';
import { BossFightGroup, TbcFightGroupable } from './types';

export const TBC_ANALYZER_STORAGE = {
    logUrls: 'wow_calcs.tbcAnalyzer.logUrls',
    fightFilter: 'wow_calcs.tbcAnalyzer.fightFilter',
    selectedEncounterIds: 'wow_calcs.tbcAnalyzer.selectedEncounterIds',
    selectedPlayerName: 'wow_calcs.tbcAnalyzer.selectedPlayerName',
    minDurationSeconds: 'wow_calcs.tbcAnalyzer.minDurationSeconds',
} as const;

export function parseLogUrls(text: string): string[] {
    return text
        .split(/[\n,]+/)
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
}

export function readStoredString(key: string, fallback = ''): string {
    try {
        return localStorage.getItem(key) ?? fallback;
    } catch {
        return fallback;
    }
}

export function writeStoredString(key: string, value: string): void {
    try {
        if (value.trim().length === 0) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
    } catch {
        // Ignore quota / private-mode failures
    }
}

export function readStoredEncounterIds(): number[] {
    try {
        const raw = localStorage.getItem(TBC_ANALYZER_STORAGE.selectedEncounterIds);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0);
    } catch {
        return [];
    }
}

export function writeStoredEncounterIds(ids: number[]): void {
    try {
        localStorage.setItem(TBC_ANALYZER_STORAGE.selectedEncounterIds, JSON.stringify(ids));
    } catch {
        // Ignore quota / private-mode failures
    }
}

export function readStoredFightFilter(): SpellUsageFightFilter {
    const stored = readStoredString(TBC_ANALYZER_STORAGE.fightFilter, 'kills');
    if (stored === 'kills' || stored === 'encounters' || stored === 'everything' || stored.startsWith('boss:')) {
        return stored as SpellUsageFightFilter;
    }
    return 'kills';
}

export function formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function formatNumber(num: number, decimals = 0): string {
    return num.toLocaleString(undefined, {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
    });
}

export function fightFilterLabel(
    filter: SpellUsageFightFilter,
    bosses: SpellUsageBossOption[],
    labelFromResult?: string
): string {
    if (labelFromResult) {
        return labelFromResult;
    }
    if (filter === 'kills') {
        return 'All kills';
    }
    if (filter === 'encounters') {
        return 'All encounters';
    }
    if (filter === 'everything') {
        return 'Everything';
    }
    const boss = bosses.find((entry) => `boss:${entry.encounterId}` === filter);
    if (boss) {
        return boss.name;
    }
    return filter;
}

export function isBossWipe(fight: TbcFightGroupable): boolean {
    return fight.encounterId > 0 && fight.kill === false;
}

export function dedupeRemovedLabel(duplicatesRemoved: number): string {
    if (duplicatesRemoved <= 0) {
        return '';
    }
    return ` Removed ${duplicatesRemoved} duplicate pull${duplicatesRemoved === 1 ? '' : 's'} across logs.`;
}

const ORPHAN_TRASH_GROUP_KEY = 'orphan-trash';

function trashGroupKeyForBoss(encounterId: number): string {
    return `trash:enc:${encounterId}`;
}

function bossGroupKeyForEncounter(encounterId: number): string {
    return `enc:${encounterId}`;
}

function isTrashFight(fight: TbcFightGroupable): boolean {
    return fight.encounterId <= 0;
}

function absoluteFightStart(fight: TbcFightGroupable): number {
    const reportStart = fight.reportStartTime ?? 0;
    const fightStart = fight.fightStartTime ?? 0;
    if (reportStart > 0 && fightStart >= 0) {
        return reportStart + fightStart;
    }
    return fightStart;
}

/** Within each report, attach trash to the next boss fight by fightStartTime. */
function trashGroupKeyByFollowingBoss<T extends TbcFightGroupable>(fights: T[]): Map<T, string> {
    const assignment = new Map<T, string>();
    const byReport = new Map<string, T[]>();

    for (const fight of fights) {
        const reportId = fight.reportId ?? '';
        const existing = byReport.get(reportId) ?? [];
        existing.push(fight);
        byReport.set(reportId, existing);
    }

    for (const reportFights of Array.from(byReport.values())) {
        const sorted = [...reportFights].sort((a, b) => {
            const aStart = a.fightStartTime ?? 0;
            const bStart = b.fightStartTime ?? 0;
            return aStart - bStart;
        });

        for (let index = 0; index < sorted.length; index += 1) {
            const fight = sorted[index];
            if (!isTrashFight(fight)) {
                continue;
            }

            let followingBoss: T | undefined;
            for (let nextIndex = index + 1; nextIndex < sorted.length; nextIndex += 1) {
                if (sorted[nextIndex].encounterId > 0) {
                    followingBoss = sorted[nextIndex];
                    break;
                }
            }

            if (followingBoss) {
                assignment.set(fight, trashGroupKeyForBoss(followingBoss.encounterId));
            } else {
                assignment.set(fight, ORPHAN_TRASH_GROUP_KEY);
            }
        }
    }

    return assignment;
}

export function groupFightsByBoss<T extends TbcFightGroupable>(fights: T[]): BossFightGroup<T>[] {
    const bossNameByEncounterId = new Map<number, string>();
    for (const fight of fights) {
        if (fight.encounterId > 0) {
            bossNameByEncounterId.set(fight.encounterId, fight.fightName);
        }
    }

    const trashAssignment = trashGroupKeyByFollowingBoss(fights);
    const groupsByKey = new Map<string, BossFightGroup<T>>();

    for (const fight of fights) {
        let key: string;
        let encounterId: number;
        let name: string;

        if (isTrashFight(fight)) {
            key = trashAssignment.get(fight) ?? ORPHAN_TRASH_GROUP_KEY;
            if (key === ORPHAN_TRASH_GROUP_KEY) {
                encounterId = 0;
                name = 'Trash';
            } else {
                const bossEncounterId = Number(key.replace('trash:enc:', ''));
                encounterId = bossEncounterId;
                const bossName = bossNameByEncounterId.get(bossEncounterId) ?? fight.fightName;
                name = `${bossName} trash`;
            }
        } else {
            key = bossGroupKeyForEncounter(fight.encounterId);
            encounterId = fight.encounterId;
            name = fight.fightName;
        }

        const existing = groupsByKey.get(key);
        if (existing) {
            existing.fights.push(fight);
        } else {
            groupsByKey.set(key, {
                key,
                name,
                encounterId,
                fights: [fight],
            });
        }
    }

    const groups = Array.from(groupsByKey.values());
    groups.sort((a, b) => {
        const aOrphan = a.key === ORPHAN_TRASH_GROUP_KEY ? 1 : 0;
        const bOrphan = b.key === ORPHAN_TRASH_GROUP_KEY ? 1 : 0;
        if (aOrphan !== bOrphan) {
            return aOrphan - bOrphan;
        }
        const aMin = Math.min(...a.fights.map((fight) => absoluteFightStart(fight)));
        const bMin = Math.min(...b.fights.map((fight) => absoluteFightStart(fight)));
        return aMin - bMin;
    });

    for (const group of groups) {
        group.fights.sort((a, b) => {
            const aAbs = absoluteFightStart(a);
            const bAbs = absoluteFightStart(b);
            if (aAbs !== bAbs) {
                return aAbs - bAbs;
            }
            if (a.kill !== undefined && b.kill !== undefined && a.kill !== b.kill) {
                return a.kill ? -1 : 1;
            }
            const aDuration = a.durationSeconds ?? 0;
            const bDuration = b.durationSeconds ?? 0;
            return bDuration - aDuration;
        });
    }

    return groups;
}

/** Group rows keyed by boss/fight name (for spell per-fight metrics keyed by name). */
export function groupRowsByName<T extends { fightName: string }>(rows: T[]): Array<{ name: string; rows: T[] }> {
    const byName = new Map<string, T[]>();
    for (const row of rows) {
        const existing = byName.get(row.fightName) ?? [];
        existing.push(row);
        byName.set(row.fightName, existing);
    }
    return Array.from(byName.entries())
        .map(([name, groupedRows]) => ({ name, rows: groupedRows }))
        .sort((a, b) => a.name.localeCompare(b.name));
}
